import React, { useEffect, useMemo, useRef, useState } from "react";
import { CalendarClock, GraduationCap, Cable, LineChart } from "lucide-react";
import { useT } from "../context/LanguageContext";

const stepIcons = [CalendarClock, GraduationCap, Cable, LineChart];
const stepStyles = [
  { accent: "#4B6FD4", badge: "#4B6FD4" },
  { accent: "#FF7A7A", badge: "#FF7A7A" },
  { accent: "#14B8A6", badge: "#14B8A6" },
  { accent: "#8B5CF6", badge: "#8B5CF6" },
];

const ProcessSection = () => {
  const t = useT();
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  const [active, setActive] = useState(0);
  const processSteps = useMemo(() => [
    { step: "01", title: t("process.step1.title"), description: t("process.step1.description") },
    { step: "02", title: t("process.step2.title"), description: t("process.step2.description") },
    { step: "03", title: t("process.step3.title"), description: t("process.step3.description") },
    { step: "04", title: t("process.step4.title"), description: t("process.step4.description") },
  ], [t]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => e.isIntersecting && (setVisible(true), obs.disconnect()),
      { threshold: 0.15 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!visible) return;
    const t = setInterval(
      () => setActive((i) => (i + 1) % processSteps.length),
      2400
    );
    return () => clearInterval(t);
  }, [visible, processSteps.length]);

  return (
    <section
      id="process"
      ref={ref}
      className={`section-fade ${visible ? "visible" : ""} px-6 flex items-center`}
      style={{
        minHeight: "100vh",
        background: "var(--section-surface-1)",
      }}
    >
      <div className="max-w-6xl mx-auto w-full" style={{ paddingTop: "4rem", paddingBottom: "4rem" }}>
        <div className="flex flex-col items-center text-center mb-16">
          <span
            className="inline-block px-3 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wider mb-4"
            style={{
              background: "linear-gradient(135deg, rgba(75,111,212,0.12), rgba(125,158,240,0.08))",
              border: "1px solid rgba(75,111,212,0.2)",
              color: "var(--brand-cyan)",
            }}
          >
            {t("process.eyebrow")}
          </span>
          <h2
            className="font-bold tracking-tight"
            style={{
              color: "var(--fg)",
              fontSize: "clamp(2rem, 4.2vw, 3.25rem)",
            }}
          >
            {t("process.title1")}{" "}
            <span className="brand-gradient-text">{t("process.title2")}</span>
            {t("process.title3")}
          </h2>
          <p
            className="mt-4 max-w-2xl text-[15px]"
            style={{ color: "var(--fg)", opacity: 0.92 }}
          >
            {t("process.subtitle")}
          </p>
        </div>

        {/* Process visual image */}
        <div className="mb-12 rounded-2xl overflow-hidden" style={{ border: "1px solid var(--border)" }}>
          <img
            src="/images/pages/hero-process.jpg"
            alt="İş süreci ve planlama"
            className="w-full h-auto object-cover"
            style={{ maxHeight: "300px" }}
            loading="lazy"
          />
        </div>

        {/* Animated horizontal progress line */}
        <div className="relative">
          <div
            className="hidden md:block absolute top-[44px] left-0 right-0 h-px"
            style={{ background: "var(--border)" }}
          />
          <div
            className="hidden md:block absolute top-[44px] left-0 h-px"
            style={{
              background:
                "linear-gradient(90deg, var(--brand-cyan), var(--brand-coral), var(--brand-navy))",
              width: `${((active + 1) / processSteps.length) * 100}%`,
              transition: "width 0.6s ease",
            }}
          />

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {processSteps.map((s, i) => {
              const Icon = stepIcons[i] || CalendarClock;
              const isActive = active === i;
              const style = stepStyles[i] || stepStyles[0];
              return (
                <div key={s.step} className="relative">
                  {/* Circular step indicator */}
                  <div className="flex justify-center mb-6">
                    <div
                      className="relative w-[88px] h-[88px] rounded-full flex items-center justify-center transition-all duration-500"
                      style={{
                        background: "var(--bg)",
                        border: `2px solid ${isActive ? style.accent : "var(--border)"}`,
                      }}
                    >
                      <Icon
                        size={28}
                        style={{
                          color: isActive ? style.accent : "var(--muted)",
                          transition: "color 0.4s ease",
                        }}
                      />
                      <span
                        className="absolute -top-1 -right-1 text-[10px] font-bold w-6 h-6 rounded-full flex items-center justify-center"
                        style={{
                          background: style.badge,
                          color: "#fff",
                          fontFamily: "ui-monospace, monospace",
                        }}
                      >
                        {s.step}
                      </span>
                    </div>
                  </div>

                  <div
                    className="text-center p-5 rounded-2xl transition-all duration-500 h-full neon-box"
                    style={{
                      background: "var(--card-bg)",
                      border: `1px solid ${isActive ? style.accent : "var(--border)"}`,
                    }}
                  >
                    <h3
                      className="text-[16px] font-semibold mb-2"
                      style={{ color: "var(--fg)" }}
                    >
                      {s.title}
                    </h3>
                    <p
                      className="text-[13.5px] leading-relaxed"
                      style={{ color: "var(--fg)", opacity: 0.9 }}
                    >
                      {s.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <div aria-hidden="true" style={{ height: "7rem" }} />
    </section>
  );
};

export default ProcessSection;
